import React, { useMemo } from 'react';
import Props from '../../Components/Props';
import DataGrid from 'components/DataGrid';
import DataGridColumnHeader from 'components/DataGridColumnHeader';
import useFilters from 'hooks/useFilters';
import DataGridExpandCell from 'components/DataGridExpandCell';
import JSXCode from 'pages/Document/Components/JSXCode';

const TabDataGrid = () => {
  const { filters, handleSorting, sorting, expanded, setExpanded } = useFilters({
    sortField: '',
    sortDirection: '',
  });

  const data = [
    {
      name: 'Student 1',
      age: 18,
      class: '10A',
    },
    {
      name: 'Student 2',
      age: 34,
      class: '11C',
    },
    {
      name: 'Student 3',
      age: 16,
      class: '12B',
    },
  ];

  const columns = useMemo(() => {
    return [
      {
        id: 'expandable',
        enableResizing: false,
        header: ({ column }) => <div className='tw-w-[34px]' />,
        cell: DataGridExpandCell,
        enableHiding: false,
        enableSorting: false,
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: ({ column }) => <DataGridColumnHeader column={column} title='Name' />,
      },
      {
        id: 'age',
        accessorKey: 'age',
        header: ({ column }) => <DataGridColumnHeader column={column} title='Age' />,
      },
      {
        id: 'class',
        accessorKey: 'class',
        header: ({ column }) => <DataGridColumnHeader column={column} title='Class' />,
        enableSorting: false,
      },
    ];
  }, []);

  return (
    <div className='component:TabDataGrid'>
      <code className='tw-block tw-mb-3'>{JSON.stringify({ filters })}</code>
      <DataGrid
        dataSource={{
          data: data,
          isFetching: false,
        }}
        columns={columns}
        manualControl={{
          sorting,
          setSorting: handleSorting,
          expanded,
          setExpanded,
        }}
        hideColumnView
        expandableRowRenderer={(row) => {
          return <div>Row expandable - {JSON.stringify({ row })}</div>;
        }}
      />

      <div className='tw-mt-4'>
        <JSXCode
          code={`const { filters, handleSorting, sorting, expanded, setExpanded } = useFilters({
    sortField: '',
    sortDirection: '',
});

  const data = [
    {
      name: 'Student 1',
      age: 18,
      class: '10A',
    },
    {
      name: 'Student 2',
      age: 34,
      class: '11C',
    },
    {
      name: 'Student 3',
      age: 16,
      class: '12B',
    },
  ];

  const columns = useMemo(() => {
    return [
      {
            id: 'expandable',
            enableResizing: false,
            header: ({ column }) => <div className='tw-w-[34px]' />,
            cell: DataGridExpandCell,
            enableHiding: false,
            enableSorting: false,
      },
      {
            id: 'name',
            accessorKey: 'name',
            header: ({ column }) => <DataGridColumnHeader column={column} title='Name' />,
      },
      {
            id: 'age',
            accessorKey: 'age',
            header: ({ column }) => <DataGridColumnHeader column={column} title='Age' />,
      },
      {
            id: 'class',
            accessorKey: 'class',
            header: ({ column }) => <DataGridColumnHeader column={column} title='Class' />,
            enableSorting: false,
      },
    ];
  }, []);

  return (
    <DataGrid
        dataSource={{
            data: data,
            isFetching: false,
        }}
        columns={columns}
        manualControl={{
            sorting,
            setSorting: handleSorting,
            expanded,
            setExpanded,
        }}
        hideColumnView
        expandableRowRenderer={(row) => {
            return <div>Row expandable - {JSON.stringify({ row })}</div>;
        }}
    />
  )
`}
        />
      </div>

      <Props
        className='tw-mt-5'
        source={[
          {
            prop: 'dataSource',
            type: (
              <pre className='tw-max-w-max dark:tw-text-white tw-text-black tw-text-sm'>
                {JSON.stringify(
                  {
                    data: 'any[]',
                    isFetching: 'boolean',
                    hasMore: 'boolean',
                    loadingMore: 'boolean',
                    fetchNextPage: '() => void',
                  },
                  null,
                  2
                )}
              </pre>
            ),
            default: 'undefined',
          },
          {
            prop: 'columns',
            type: (
              <>
                {JSON.stringify(
                  [
                    {
                      id: 'string',
                      accessorKey: 'string',
                      enableSorting: 'boolean',
                      header: '({ column }: any) => JSX | string',
                      cell: '(rowProps: any) => void',
                    },
                  ],
                  null,
                  2
                )}
              </>
            ),
            default: 'undefined',
          },
          {
            prop: 'manualControl',
            type: (
              <>
                {JSON.stringify(
                  {
                    sorting: '{ id: string; desc: boolean }[]',
                    setSorting: '(nextSorting) => void',
                  },
                  null,
                  2
                )}
              </>
            ),
            default: 'undefined',
          },
          {
            prop: 'hideColumnView',
            type: 'boolean',
            default: 'false',
          },
          {
            prop: 'leftPinningColumns',
            type: 'string[]',
            default: 'undefined',
          },
          {
            prop: 'rightPinningColumns',
            type: 'string[]',
            default: 'undefined',
          },
          {
            prop: 'className',
            type: 'string',
            default: 'undefined',
          },
          {
            prop: 'tableClassName',
            type: 'string',
            default: 'undefined',
          },
          { prop: 'rowIdPath', type: 'string', default: 'undefined' },
          { prop: 'expandableRowRenderer', type: '(row: any) => JSX', default: 'undefined' },
        ]}
      />
    </div>
  );
};

export default TabDataGrid;
